import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Search } from 'lucide-react';
import { useSubagentsList } from '@/features/subagents/hooks';
import { Card, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { useAuth } from '@/features/auth/AuthContext';

export default function SubagentsListPage() {
  const { user } = useAuth();
  const [search, setSearch] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const debouncedSearch = useDebounced(search, 300);
  const query = useSubagentsList({ search: debouncedSearch, tags, page: 1, pageSize: 30 });

  const canUpload = user?.role === 'admin' || user?.role === 'editor';

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold">Subagentes</h1>
          <p className="text-text-muted text-sm">
            Repositório de subagentes compartilhados pelo time.
          </p>
        </div>
        {canUpload && (
          <Link to="/subagentes/novo">
            <Button>
              <Plus size={16} /> Novo subagente
            </Button>
          </Link>
        )}
      </header>

      <Card>
        <CardBody className="flex flex-col md:flex-row gap-3">
          <div className="flex-1 relative">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted"
            />
            <Input
              className="pl-9"
              placeholder="Buscar por nome ou descrição..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <TagFilter tags={tags} onChange={setTags} />
        </CardBody>
      </Card>

      {query.isLoading ? (
        <div className="flex justify-center py-12 text-text-muted">
          <Spinner size={24} />
        </div>
      ) : query.isError ? (
        <p className="text-red-400">Erro ao carregar subagentes.</p>
      ) : query.data && query.data.items.length === 0 ? (
        <Card>
          <CardBody className="text-center text-text-muted py-12">
            Nenhum subagente encontrado.
          </CardBody>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {query.data!.items.map((s) => (
            <Link key={s.id} to={`/subagentes/${s.id}`} className="group">
              <Card className="hover:border-brand-400 transition h-full">
                <CardBody>
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-semibold text-text-primary group-hover:text-brand-400">
                      {s.name}
                    </h3>
                    <Badge tone="brand">v{s.version}</Badge>
                  </div>
                  <p className="text-sm text-text-secondary line-clamp-3 mb-3">
                    {s.description}
                  </p>
                  <div className="flex flex-wrap gap-1 mb-3">
                    {s.model && <Badge tone="info">{s.model}</Badge>}
                    {s.tools.map((t) => (
                      <Badge key={t} tone="warn">
                        {t}
                      </Badge>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-1 mb-3">
                    {s.tags.map((t) => (
                      <Badge key={t.id} tone="neutral">
                        #{t.name}
                      </Badge>
                    ))}
                  </div>
                  <div className="text-xs text-text-muted">
                    {s.uploader.displayName || s.uploader.email}
                  </div>
                </CardBody>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

function TagFilter({ tags, onChange }: { tags: string[]; onChange: (next: string[]) => void }) {
  const [draft, setDraft] = useState('');
  return (
    <div className="flex items-center gap-2 flex-wrap">
      {tags.map((t) => (
        <button
          key={t}
          onClick={() => onChange(tags.filter((x) => x !== t))}
          className="px-2 py-1 rounded-full bg-brand-500/15 text-brand-300 text-xs hover:bg-brand-500/25"
        >
          #{t} ×
        </button>
      ))}
      <input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && draft.trim()) {
            e.preventDefault();
            const t = draft.trim().toLowerCase();
            if (!tags.includes(t)) onChange([...tags, t]);
            setDraft('');
          }
        }}
        placeholder="filtrar por tag (enter)"
        className="px-3 py-2 rounded-lg border border-border bg-surface-raised text-sm w-44"
      />
    </div>
  );
}
